import {
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Globe,
  KeyRound,
  LogOut,
  Menu,
  Moon,
  Sun,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { PoweredByEdx } from "@/features/auth/PoweredByEdx";
import { useAuthStore } from "@/shared/auth/authStore";
import { APP_TITLE, APP_VERSION } from "@/shared/config/env";
import { NewPasswordFields } from "@/shared/components/NewPasswordFields";
import { extractTimezoneName, useUserTimezoneStore } from "@/shared/lib/datetime";
import { validateNewPassword } from "@/shared/lib/passwordRules";
import { NAV_ITEMS, canAccessNav, findNavMatch, type NavItem } from "@/shared/config/navigation";
import { useDockedSidebar } from "@/shared/hooks/useMediaQuery";
import { useSidebarCollapsed } from "@/shared/hooks/useSidebarCollapsed";
import { useTheme } from "@/shared/hooks/useTheme";
import { phonemanagerApi } from "@/shared/api/phonemanagerApi";
import { useAsyncAction } from "@/shared/hooks/useAsyncAction";
import { usePhoneManagerWs } from "@/shared/ws/usePhoneManagerWs";
import { OutboundScreenPopHost } from "@/features/outbound/OutboundScreenPopHost";
import { MiniSoftphone } from "@/features/softphone/MiniSoftphone";
import { shutdownSoftphone } from "@/features/softphone/softphoneController";
import { useSoftphoneRuntime } from "@/features/softphone/useSoftphoneRuntime";
import { useWsStore } from "@/shared/ws/wsStore";
import { Avatar } from "@/shared/ui/avatar";
import { BrandLockup, BrandMark } from "@/shared/ui/brand-mark";
import { Button } from "@/shared/ui/button";
import { Dialog } from "@/shared/ui/dialog";
import { DropdownMenu, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/shared/ui/dropdown-menu";
import { FormField } from "@/shared/ui/form-field";
import { IconButton } from "@/shared/ui/icon-button";
import { Select } from "@/shared/ui/select";
import { Tooltip } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";

function NavLinkItem({ item, collapsed, onNavigate }: { item: NavItem; collapsed: boolean; onNavigate: () => void }) {
  const Icon = item.icon;
  if (!item.to) return null;

  const link = (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      aria-label={collapsed ? item.title : undefined}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "relative flex h-9 items-center rounded-md text-body transition-colors duration-150",
          collapsed ? "w-10 justify-center" : "gap-2.5 px-2.5",
          isActive
            ? "bg-brand-subtle font-medium text-brand-accent"
            : "text-fg-muted hover:bg-surface-hover hover:text-fg",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !collapsed ? <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-brand-accent" aria-hidden /> : null}
          {Icon ? <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-brand-accent" : "text-fg-subtle")} strokeWidth={2} aria-hidden /> : null}
          {!collapsed ? <span className="truncate">{item.title}</span> : null}
        </>
      )}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip content={item.title} side="right" delay={0}>
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function SidebarNav({
  collapsed,
  includeAnyRole,
  onNavigate,
}: {
  collapsed: boolean;
  includeAnyRole: (r: string[]) => boolean;
  onNavigate: () => void;
}) {
  return (
    <nav aria-label="Menu principal" className={cn("flex flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden py-3", collapsed ? "items-center px-2" : "px-3")}>
      {NAV_ITEMS.filter((item) => canAccessNav(item, includeAnyRole)).map((group) => {
        const children = (group.children ?? [group]).filter((c) => includeAnyRole(c.roles));
        if (!children.length) return null;
        return (
          <div key={group.title} className={cn("flex flex-col gap-0.5", collapsed && "items-center")}>
            {group.children ? (
              collapsed ? (
                <span className="mx-auto mb-1 h-px w-6 bg-border first:hidden" aria-hidden />
              ) : (
                <p className="mb-1 px-2.5 text-overline uppercase text-fg-subtle">{group.title}</p>
              )
            ) : null}
            {children.map((child) => (
              <NavLinkItem key={child.to} item={child} collapsed={collapsed} onNavigate={onNavigate} />
            ))}
          </div>
        );
      })}
    </nav>
  );
}

function ConnectionStatus({ online, pendingConfig }: { online: boolean; pendingConfig: boolean }) {
  const state = !online ? "offline" : pendingConfig ? "pending" : "online";
  const meta = {
    online: { label: "Tempo real conectado", short: "Conectado", dot: "bg-success", text: "text-fg-muted" },
    pending: { label: "Conectado — configuração pendente de aplicação", short: "Config. pendente", dot: "bg-warning", text: "text-warning-fg" },
    offline: { label: "Sem conexão em tempo real — tentando reconectar", short: "Reconectando", dot: "bg-danger animate-pulse", text: "text-danger-fg" },
  }[state];
  return (
    <Tooltip content={meta.label} side="bottom">
      <span
        role="status"
        aria-label={meta.label}
        tabIndex={0}
        className={cn("inline-flex h-8 items-center gap-2 rounded-full border border-border bg-surface px-2.5 text-caption font-medium", meta.text)}
      >
        <span className={cn("h-2 w-2 shrink-0 rounded-full", meta.dot)} aria-hidden />
        <span className="hidden sm:inline">{meta.short}</span>
      </span>
    </Tooltip>
  );
}

export function AppShell() {
  const { run, busy } = useAsyncAction();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const docked = useDockedSidebar();
  const { collapsed, toggle: toggleSidebar } = useSidebarCollapsed();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Ao ancorar a sidebar (xl+), o drawer móvel é fechado (ajuste durante o render, sem efeito).
  if (docked && drawerOpen) setDrawerOpen(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const setUserTimezone = useUserTimezoneStore((s) => s.setTimezone);

  // Fuso do usuário logado: aplicado a todas as datas renderizadas (paridade GUI store/user).
  const { data: userTz } = useQuery({
    queryKey: ["user-timezone"],
    queryFn: () => phonemanagerApi.getUserTimeZone(),
    staleTime: 10 * 60_000,
  });
  useEffect(() => {
    setUserTimezone(extractTimezoneName(userTz));
  }, [userTz, setUserTimezone]);
  const [timezone, setTimezone] = useState("");
  const [language, setLanguage] = useState("");

  const { data: timeZoneOptions = [] } = useQuery({
    queryKey: ["timezones"],
    queryFn: () => phonemanagerApi.getTimeZones(),
    enabled: tzOpen,
  });

  const { data: languageOptions = [] } = useQuery({
    queryKey: ["languages"],
    queryFn: () => phonemanagerApi.getSupportedLanguages(),
    enabled: langOpen,
  });

  useEffect(() => {
    if (!tzOpen) return;
    phonemanagerApi
      .getUserTimeZone()
      .then((tz) => setTimezone(extractTimezoneName(tz) ?? ""))
      .catch(() => {});
  }, [tzOpen]);

  useEffect(() => {
    if (!langOpen) return;
    phonemanagerApi
      .getLanguageByLogin()
      .then((lang) => setLanguage(String(lang ?? "")))
      .catch(() => {});
  }, [langOpen]);

  const displayName = useAuthStore((s) => s.displayName);
  const subject = useAuthStore((s) => s.tokenSubject);
  const includeAnyRole = useAuthStore((s) => s.includeAnyRole);
  const logout = useAuthStore((s) => s.logout);

  const online = useWsStore((s) => s.statusOnline);
  const pendingConfig = useWsStore((s) => s.statusUpdateConfig);

  usePhoneManagerWs(true);
  // Softphone WebRTC do agente (registro SIP, heartbeat, mensagens do servidor) — uma vez por app.
  useSoftphoneRuntime();

  const username = !displayName || displayName === subject ? subject : displayName;
  const current = findNavMatch(pathname);

  const doLogout = () => {
    void shutdownSoftphone("Logout").finally(() => {
      logout();
      navigate("/login");
    });
  };

  const sidebarContent = (mobileDrawer: boolean) => {
    const isCollapsed = mobileDrawer ? false : collapsed;
    return (
      <>
        <div className={cn("flex h-14 shrink-0 items-center border-b border-border", isCollapsed ? "justify-center px-2" : "justify-between gap-2 px-4")}>
          <Link to="/" className="flex min-w-0 items-center rounded-md" onClick={() => setDrawerOpen(false)} aria-label={`${APP_TITLE} — início`}>
            {isCollapsed ? <BrandMark size="sm" /> : <BrandLockup size="sm" />}
          </Link>
          {mobileDrawer ? <IconButton icon={X} label="Fechar menu" size="sm" tooltip={false} onClick={() => setDrawerOpen(false)} /> : null}
        </div>

        <SidebarNav collapsed={isCollapsed} includeAnyRole={includeAnyRole} onNavigate={() => setDrawerOpen(false)} />

        <div className={cn("flex shrink-0 flex-col gap-2 border-t border-border", isCollapsed ? "items-center px-2 py-3" : "px-3 py-3")}>
          {!mobileDrawer ? (
            isCollapsed ? (
              <IconButton icon={ChevronsRight} label="Expandir menu" tooltipSide="right" onClick={toggleSidebar} aria-expanded={false} />
            ) : (
              <Button variant="ghost" size="sm" icon={ChevronsLeft} onClick={toggleSidebar} aria-expanded className="justify-start px-2.5 text-fg-subtle">
                Recolher menu
              </Button>
            )
          ) : null}
          {!isCollapsed ? (
            <div className="flex items-center justify-between gap-2 px-2.5 pt-1">
              <PoweredByEdx compact />
              <span className="tnum text-caption text-fg-subtle">v{APP_VERSION}</span>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <div className="flex min-h-full w-full max-w-full bg-canvas">
      <a
        href="#conteudo"
        className="sr-only z-[300] rounded-md bg-surface px-3 py-2 text-body font-medium text-fg shadow-md focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Pular para o conteúdo
      </a>

      {/* Drawer (< 1280px): não ocupa largura do layout */}
      {drawerOpen && !docked ? (
        <div className="fixed inset-0 z-40 bg-overlay animate-fade-in xl:hidden" aria-hidden onClick={() => setDrawerOpen(false)} />
      ) : null}
      <aside
        aria-label="Navegação"
        aria-hidden={!drawerOpen || undefined}
        inert={!drawerOpen || undefined}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[min(17rem,86vw)] flex-col border-r border-border bg-surface shadow-lg transition-transform duration-200 ease-out xl:hidden",
          drawerOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {sidebarContent(true)}
      </aside>

      {/* Sidebar fixa — xl+ (1280px) */}
      <aside
        aria-label="Navegação"
        className={cn(
          "sticky top-0 hidden h-[100dvh] shrink-0 flex-col overflow-hidden border-r border-border bg-surface transition-[width] duration-200 ease-out xl:flex",
          collapsed ? "w-[var(--pm-sidebar-w-collapsed)]" : "w-[var(--pm-sidebar-w)]",
        )}
      >
        {sidebarContent(false)}
      </aside>

      <div className="relative flex min-h-full w-full min-w-0 max-w-full flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 min-w-0 shrink-0 items-center gap-2 border-b border-border bg-surface/90 px-3 backdrop-blur-md sm:px-4 lg:px-6">
          <IconButton icon={Menu} label="Abrir menu" className="xl:hidden" tooltip={false} onClick={() => setDrawerOpen(true)} />

          <nav aria-label="Local" className="min-w-0 flex-1">
            {current ? (
              <p className="flex min-w-0 items-center gap-1.5 truncate text-body">
                {current.group ? (
                  <>
                    <span className="hidden text-fg-subtle sm:inline">{current.group}</span>
                    <span className="hidden text-fg-disabled sm:inline" aria-hidden>
                      /
                    </span>
                  </>
                ) : null}
                <span className="truncate font-medium text-fg">{current.item.title}</span>
              </p>
            ) : null}
          </nav>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <ConnectionStatus online={online} pendingConfig={Boolean(pendingConfig)} />

            <IconButton
              icon={theme === "dark" ? Sun : Moon}
              label={theme === "dark" ? "Usar tema claro" : "Usar tema escuro"}
              tooltipSide="bottom"
              onClick={toggle}
            />

            <DropdownMenu
              label="Menu do usuário"
              minWidth="15rem"
              trigger={
                <button
                  type="button"
                  aria-label={`Conta de ${username ?? "usuário"}`}
                  className="flex h-9 items-center gap-2 rounded-md py-1 pl-1 pr-1 transition-colors hover:bg-surface-hover sm:pr-2.5"
                >
                  <Avatar name={username} size="sm" />
                  <span className="hidden max-w-[10rem] truncate text-body font-medium text-fg sm:inline">{username}</span>
                </button>
              }
            >
              <DropdownMenuLabel>
                <span className="block truncate normal-case tracking-normal text-body-sm font-medium text-fg">{username}</span>
                <span className="block truncate font-normal normal-case tracking-normal">{subject}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={KeyRound} onSelect={() => setPwdOpen(true)}>
                Minha conta e senha
              </DropdownMenuItem>
              <DropdownMenuItem icon={Clock} onSelect={() => setTzOpen(true)}>
                Fuso horário
              </DropdownMenuItem>
              <DropdownMenuItem icon={Globe} onSelect={() => setLangOpen(true)}>
                Idioma
              </DropdownMenuItem>
              <DropdownMenuItem icon={theme === "dark" ? Sun : Moon} onSelect={toggle}>
                {theme === "dark" ? "Tema claro" : "Tema escuro"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={LogOut} tone="danger" onSelect={doLogout}>
                Sair
              </DropdownMenuItem>
            </DropdownMenu>
          </div>
        </header>

        <main id="conteudo" tabIndex={-1} className="relative min-w-0 flex-1 px-4 py-5 outline-none sm:px-6 sm:py-6 lg:px-8">
          <div className="mx-auto w-full min-w-0 max-w-[1440px]">
            <Outlet />
          </div>
        </main>
        <OutboundScreenPopHost />
        <MiniSoftphone />
      </div>

      <Dialog
        open={pwdOpen}
        title="Minha conta"
        subtitle={subject ?? undefined}
        onClose={() => {
          setPwdOpen(false);
          setPasswordTouched(false);
        }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPwdOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={busy}
              onClick={() => {
                setPasswordTouched(true);
                if (validateNewPassword(password, passwordConfirm)) return;
                void run(() => phonemanagerApi.changeUserPasswordByLogin(password), {
                  loading: "Atualizando senha…",
                  success: "Senha alterada com sucesso.",
                  error: "Não foi possível alterar a senha.",
                  onSuccess: () => {
                    setPwdOpen(false);
                    setPassword("");
                    setPasswordConfirm("");
                    setPasswordTouched(false);
                  },
                });
              }}
            >
              Salvar senha
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-body-sm text-fg-subtle">Logado como {username}. Defina uma nova senha abaixo.</p>
          <NewPasswordFields
            password={password}
            confirm={passwordConfirm}
            onPasswordChange={setPassword}
            onConfirmChange={setPasswordConfirm}
            error={validateNewPassword(password, passwordConfirm)}
            showError={passwordTouched}
          />
          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            <Button variant="ghost" size="sm" icon={Clock} onClick={() => setTzOpen(true)}>
              Fuso horário
            </Button>
            <Button variant="ghost" size="sm" icon={Globe} onClick={() => setLangOpen(true)}>
              Idioma
            </Button>
            <Button variant="danger-ghost" size="sm" icon={LogOut} className="ml-auto" onClick={doLogout}>
              Sair
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={tzOpen}
        title="Fuso horário"
        size="sm"
        onClose={() => setTzOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTzOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={busy}
              onClick={() =>
                run(() => phonemanagerApi.setUserTimeZone(timezone), {
                  loading: "Salvando fuso horário…",
                  success: "Fuso horário atualizado com sucesso.",
                  error: "Não foi possível salvar o fuso horário.",
                  invalidateKeys: [["user-timezone"]],
                  onSuccess: () => {
                    setTzOpen(false);
                    setUserTimezone(timezone || null);
                  },
                })
              }
            >
              Salvar
            </Button>
          </>
        }
      >
        <FormField label="Fuso horário" hint="Todas as datas e horários do sistema passam a ser exibidos neste fuso.">
          <Select value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="Selecione…" options={timeZoneOptions} />
        </FormField>
      </Dialog>

      <Dialog
        open={langOpen}
        title="Idioma"
        size="sm"
        onClose={() => setLangOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLangOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={busy}
              onClick={() =>
                run(() => phonemanagerApi.setLanguageByLogin(language), {
                  loading: "Salvando idioma…",
                  success: "Idioma atualizado com sucesso.",
                  error: "Não foi possível salvar o idioma.",
                  onSuccess: () => setLangOpen(false),
                })
              }
            >
              Salvar
            </Button>
          </>
        }
      >
        <FormField label="Idioma">
          <Select value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="Selecione…" options={languageOptions} />
        </FormField>
      </Dialog>
    </div>
  );
}
